// ═══════════════════════════════════════
// ATLAS BEASTS — APP ROOT
// ═══════════════════════════════════════

import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthState, AuthContext } from './store/auth'
import { useUIStore } from './store/uiStore'
import { ToastProvider } from './components/ui/Toast'
import { playMusic, playSFX } from './lib/audio'
import type { Player } from './types'
import Landing from './pages/Landing'
import Login from './pages/Login'
import SeasonPublic from './pages/SeasonPublic'
import CreatureVault from './pages/CreatureVault'
import BountyBoard from './pages/BountyBoard'
import Marketplace from './pages/Marketplace'
import Settings from './pages/Settings'
import Season from './pages/Season'
import Inventory from './pages/Inventory'
import PVP from './pages/PVP'
import Battle from './pages/Battle'
import Shop from './pages/Shop'
import Leaderboards from './pages/Leaderboards'
import CharacterRoster from './pages/CharacterRoster'
import Loading from './components/ui/Loading'
import PlayerProfile from './components/ui/PlayerProfile'
import OfflineBanner from './components/ui/OfflineBanner'
import UpdatePrompt from './components/ui/UpdatePrompt'

const WorldMap = lazy(() => import('./pages/WorldMap'))
const ZoneDetail = lazy(() => import('./pages/ZoneDetail'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      throwOnError: false,
      retry: (failureCount) => {
        if (!navigator.onLine) return false
        return failureCount < 2
      },
    },
  },
})

export default function App() {
  const { state, initialize, setAuth, signOut, refreshPlayer } = useAuthState()
  const musicStarted = useRef(false)

  useEffect(() => {
    if (state.isAuthenticated && state.player && !musicStarted.current) {
      musicStarted.current = true
      playMusic('theme-main')
    }
  }, [state.isAuthenticated, state.player])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const playerData = params.get('player')

    if (token && playerData) {
      try {
        const player: Player = JSON.parse(decodeURIComponent(playerData))
        setAuth(player, token)
        window.history.replaceState({}, '', '/')
        return
      } catch {
        // fall through
      }
    }

    initialize()
  }, [initialize, setAuth])

  if (state.isLoading) return <Loading />
  
  // Allow landing page without auth, require auth for everything else
  const publicRoutes = ['/landing', '/']
  if (!state.isAuthenticated && !publicRoutes.includes(window.location.pathname)) {
    return <Login onAuth={setAuth} />
  }

  return (
    <AuthContext.Provider value={{
      player: state.player,
      token: state.token,
      isLoading: state.isLoading,
      isAuthenticated: state.isAuthenticated,
      setAuth,
      signOut,
      refreshPlayer,
    }}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </AuthContext.Provider>
  )
}

function AppShell() {
  const profileOpen = useUIStore((s: any) => s.profileOpen)
  const setProfileOpen = useUIStore((s: any) => s.setProfileOpen)
  const { setAuth } = useAuthState()

  return (
    <div className="w-full bg-ab-deep relative" style={{ overflowX: 'hidden', minHeight: '100vh' }}>
      {/* Main Content - Landing First UX */}
      <Routes>
        <Route path="/" element={<Navigate to="/landing" replace />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/login" element={<Login onAuth={setAuth} />} />
        <Route path="/season" element={<SeasonPublic />} />
        <Route path="/map" element={<WorldMap />} />
        <Route path="/bounties" element={
          <OverlayPage><BountyBoard /></OverlayPage>
        } />
        <Route path="/crew" element={
          <OverlayPage><CharacterRoster /></OverlayPage>
        } />
        <Route path="/beasts" element={
          <OverlayPage><CreatureVault /></OverlayPage>
        } />
        <Route path="/market" element={
          <OverlayPage><Marketplace /></OverlayPage>
        } />
        <Route path="/inventory" element={
          <OverlayPage><Inventory /></OverlayPage>
        } />
        <Route path="/pvp" element={
          <OverlayPage><PVP /></OverlayPage>
        } />
        <Route path="/battle" element={
          <OverlayPage><Battle /></OverlayPage>
        } />
        <Route path="/shop" element={
          <OverlayPage><Shop /></OverlayPage>
        } />
        <Route path="/leaderboards" element={
          <OverlayPage><Leaderboards /></OverlayPage>
        } />
        <Route path="/settings" element={
          <OverlayPage><Settings /></OverlayPage>
        } />
        <Route path="/season" element={
          <OverlayPage><Season /></OverlayPage>
        } />
        <Route path="/zone/:id" element={
          <Suspense fallback={<Loading />}>
            <ZoneDetail />
          </Suspense>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Floating Icon Navigation (Pokemon Go Style) */}
      <FloatingNav onProfileOpen={() => setProfileOpen(true)} />

      {/* Player Profile Modal */}
      <PlayerProfile open={profileOpen} onClose={() => setProfileOpen(false)} />

      {/* System UI */}
      <OfflineBanner />
      <UpdatePrompt />
    </div>
  )
}

// ── Overlay Page Wrapper (Slide up from bottom like Pokemon Go) ──
function OverlayPage({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  return (
    <div className="fixed inset-0 z-[50] w-full animate-in slide-in-from-bottom duration-300">
      <div className="absolute inset-0 bg-[#0D0D15] overflow-auto w-full">
        <button
          onClick={() => navigate('/')}
          className="fixed top-4 left-4 z-[60] w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(13,13,21,0.9)', border: '1px solid rgba(58,58,90,0.5)' }}
        >
          <span className="text-[#EAF6FF]">←</span>
        </button>
        <div className="pt-16 pb-24 px-4 w-full">
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Floating Icon Navigation (Pokemon Go Style - Simplified) ──
function FloatingNav({ onProfileOpen }: { onProfileOpen: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMap, setIsMap] = useState(true)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    setIsMap(location.pathname === '/' || location.pathname === '/map')
  }, [location.pathname])

  if (!isMap) return null

  const menuItems = [
    { path: '/bounties', icon: '🎯', label: 'Hunt', color: '#FF4800', desc: 'Track bounties' },
    { path: '/crew',     icon: '⚔️', label: 'Crew', color: '#00F0FF', desc: 'Your squad' },
    { path: '/beasts',   icon: '🐾', label: 'Vault', color: '#39FF14', desc: 'Creatures' },
    { path: '/market',   icon: '⚖️', label: 'Trade', color: '#FFE800', desc: 'Marketplace' },
    { path: '/inventory', icon: '🎒', label: 'Items', color: '#BF00FF', desc: 'Your inventory' },
    { path: '/shop',     icon: '🛒', label: 'Shop', color: '#39FF14', desc: 'Buy items' },
    { path: '/pvp',      icon: '⚔️', label: 'Arena', color: '#FF4800', desc: 'PVP battles' },
    { path: '/leaderboards', icon: '🏆', label: 'Ranks', color: '#FFE800', desc: 'Leaderboards' },
    { path: '/settings', icon: '⚙️', label: 'Settings', color: '#B8EEFF', desc: 'Config' },
  ]

  return (
    <>
      {/* Top Right - Profile */}
      <div className="fixed top-4 right-4 z-[60]">
        <button
          onClick={onProfileOpen}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95 hover:scale-105 group relative"
          style={{ 
            background: 'rgba(255,232,0,0.15)', 
            border: '1px solid rgba(255,232,0,0.5)',
            boxShadow: '0 4px 24px rgba(255,232,0,0.25), 0 0 0 1px rgba(0,0,0,0.5)'
          }}
        >
          <span className="text-xl" style={{ filter: 'drop-shadow(0 0 8px #FFE800)' }}>👤</span>
          {/* Tooltip */}
          <span className="absolute right-full mr-3 px-2 py-1 rounded text-xs font-display font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none"
                style={{ background: 'rgba(13,13,21,0.95)', border: '1px solid rgba(58,58,90,0.5)', color: '#FFE800' }}>
            Profile
          </span>
        </button>
      </div>

      {/* Bottom Center - Menu Button (☰ hamburger = menu) */}
      <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-[60]">
        <button
          onClick={() => setMenuOpen(true)}
          className="w-16 h-16 rounded-full flex items-center justify-center transition-all active:scale-95 hover:scale-105 group relative"
          style={{ 
            background: 'linear-gradient(135deg, rgba(0,240,255,0.2) 0%, rgba(191,0,255,0.2) 100%)',
            border: '2px solid rgba(0,240,255,0.5)',
            boxShadow: '0 4px 30px rgba(0,240,255,0.3), inset 0 1px 0 rgba(255,255,255,0.1), 0 0 0 1px rgba(0,0,0,0.5)'
          }}
        >
          <span className="text-2xl" style={{ filter: 'drop-shadow(0 0 10px #00F0FF)' }}>☰</span>
          {/* Tooltip */}
          <span className="absolute bottom-full mb-3 px-2 py-1 rounded text-xs font-display font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap left-1/2 -translate-x-1/2"
                style={{ background: 'rgba(13,13,21,0.95)', border: '1px solid rgba(58,58,90,0.5)', color: '#00F0FF' }}>
            Menu
          </span>
        </button>
      </div>

      {/* Full Screen Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center animate-in fade-in duration-200"
             style={{ background: 'rgba(2,5,16,0.9)', backdropFilter: 'blur(8px)' }}>
          {/* Close button */}
          <button 
            onClick={() => setMenuOpen(false)}
            className="fixed top-4 left-4 w-12 h-12 rounded-full flex items-center justify-center text-2xl z-[71] transition-all active:scale-95"
            style={{ background: 'rgba(13,13,21,0.9)', border: '1px solid rgba(58,58,90,0.5)' }}
          >
            ✕
          </button>
          
          {/* Menu Grid */}
          <div className="grid grid-cols-2 gap-4 p-6 max-w-sm w-full">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => { playSFX('nav-switch'); setMenuOpen(false); navigate(item.path) }}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all active:scale-95 hover:scale-105 group"
                style={{
                  background: 'rgba(13,13,21,0.8)',
                  border: `1px solid ${item.color}40`,
                  boxShadow: `0 4px 20px ${item.color}15`
                }}
              >
                <span className="text-4xl" style={{ filter: `drop-shadow(0 0 12px ${item.color})` }}>{item.icon}</span>
                <div className="text-center">
                  <p className="font-display font-bold text-sm uppercase tracking-wider" style={{ color: item.color }}>{item.label}</p>
                  <p className="text-[10px] text-[#5A5A7A] font-mono uppercase">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
